import { Link } from "react-router-dom";
import { ArrowRight, Building2, Users, User, Briefcase } from "lucide-react";
import { FadeUp, MaskLine, Overline } from "@/components/Reveal";

const ENGAGEMENTS = [
  {
    id: "organizational-consulting",
    icon: Building2,
    title: "Organizational Consulting",
    intro:
      "For companies that suspect their customer problems, retention problems, and culture problems might actually be the same problem wearing different hats. (They usually are.)",
    items: [
      "Employee experience", "Customer experience", "Organizational culture", "Team effectiveness",
      "Employee retention", "Customer retention", "Internal communication", "Systems and workflow",
      "Leadership development", "Change management", "Business operations",
    ],
  },
  {
    id: "leadership-team-development",
    icon: Users,
    title: "Leadership & Team Development",
    intro:
      "Better leadership starts with understanding humans — including the one in the mirror. Executive coaching, team coaching, workshops, and facilitation for leaders who want to be worth following.",
    items: [
      "Executive coaching", "Leadership coaching", "Emerging leader development", "Team coaching",
      "Workshops", "Facilitation", "Communication", "Trust", "Conflict", "Accountability",
      "Ways of being", "Strength discovery", "Team effectiveness",
    ],
  },
  {
    id: "individual-coaching",
    icon: User,
    title: "Individual Coaching",
    intro:
      "One-on-one ontological coaching for people at a crossroads — or people who suspect they're standing at one and can't quite read the signs.",
    items: [
      "Career change", "Leadership", "Burnout", "Relationships with work", "Confidence",
      "Identity", "Major life transitions", "Direction", "Purpose", "Communication",
    ],
  },
  {
    id: "fractional-executive",
    icon: Briefcase,
    title: "Fractional / Executive Engagements",
    intro:
      "Sometimes you don't need advice. You need someone who can step in and help lead. I'm available for select fractional and leadership engagements.",
    items: [
      "Chief Experience Officer", "Customer experience", "Employee experience", "People & culture",
      "Organizational development", "Leadership development", "Operations", "Strategic advisory",
    ],
  },
];

const WorkWithJocelyn = () => (
  <>
    <section data-testid="work-hero" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Overline>Work With Jocelyn</Overline>
        <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.05] text-ink text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          <MaskLine>Different problems.</MaskLine>
          <MaskLine delay={0.15}>Same starting point.</MaskLine>
        </h1>
        <MaskLine delay={0.35}>
          <p className="mt-6 font-serif italic font-medium text-clay text-6xl sm:text-7xl lg:text-8xl leading-none">
            People.
          </p>
        </MaskLine>
        <FadeUp delay={0.4}>
          <p className="mt-10 max-w-2xl text-base sm:text-lg leading-relaxed text-mute">
            I work across several kinds of engagements — consulting, coaching, facilitation, and fractional
            leadership — but they all begin the same way: understanding the humans involved, then building
            from there.
          </p>
        </FadeUp>
      </div>
    </section>

    <section data-testid="engagements-section" className="pb-24 sm:pb-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-8">
        {ENGAGEMENTS.map((eng, i) => {
          const Icon = eng.icon;
          const dark = i % 2 === 1;
          return (
            <FadeUp key={eng.id}>
              <article
                data-testid={`engagement-${eng.id}`}
                className={`grid gap-8 lg:grid-cols-[1fr_1.1fr] p-8 sm:p-12 border transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(22,21,19,0.3)] ${
                  dark ? "bg-ink text-[#F5F5F7] border-white/10" : "bg-bone text-ink border-soot/10"
                }`}
              >
                <div>
                  <div className={`inline-flex p-3 rounded-full ${dark ? "bg-white/10" : "bg-blush"}`}>
                    <Icon className={`h-6 w-6 ${dark ? "text-clay" : "text-clay"}`} />
                  </div>
                  <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-medium tracking-tight leading-[1.12]">
                    {eng.title}
                  </h2>
                  <p className={`mt-5 text-base leading-relaxed ${dark ? "text-[#F5F5F7]/70" : "text-mute"}`}>
                    {eng.intro}
                  </p>
                  <Link
                    to={`/contact?topic=${encodeURIComponent(eng.title)}`}
                    data-testid={`engagement-cta-${eng.id}`}
                    className={`mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[11px] font-sans font-bold uppercase tracking-[0.22em] transition-colors duration-300 ${
                      dark
                        ? "bg-clay text-bone hover:bg-claydark"
                        : "bg-ink text-bone hover:bg-clay"
                    }`}
                  >
                    Enquire About This <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 content-center">
                  {eng.items.map((item) => (
                    <li
                      key={item}
                      className={`flex items-start gap-2.5 text-sm ${dark ? "text-[#F5F5F7]/75" : "text-mute"}`}
                    >
                      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-clay" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </FadeUp>
          );
        })}
      </div>
    </section>

    <section data-testid="work-cta" className="bg-cream py-24 sm:py-28 border-t border-soot/10">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <FadeUp>
          <p className="font-hand text-3xl text-clay -rotate-1">not sure which one fits?</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-ink leading-[1.1]">
            That's completely normal. <em className="text-clay">Start anyway.</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-mute leading-relaxed max-w-xl mx-auto">
            You don't need to know exactly what kind of engagement you need before reaching out. Tell me
            what's going on — figuring out the right shape of the work is part of the work.
          </p>
          <Link
            to="/contact"
            data-testid="work-cta-contact"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-sans font-bold uppercase tracking-[0.22em] text-bone transition-colors duration-300 hover:bg-claydark"
          >
            Start a Conversation <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </div>
    </section>
  </>
);

export default WorkWithJocelyn;
